import { v4 as uuidv4 } from 'uuid';
import { TemplateTypes } from '../../../../lib/models/TemplateTypes';
import { isLength } from '../../../../lib/util/helpers';

export const HVM_MESSAGE_VERSION = 2;

export const HvmQuestionType = {
  CHOICE: 'CHOICE',
  COUNTER: 'COUNTER',
  CHART: 'CHART',
};

let submissionId = null;

/**
 * One id per finder run: HVM upserts by it, so repeated clicks on the HVM button replace the saved result
 * instead of adding a new one. A page reload starts a new run.
 */
export function getHvmSubmissionId() {
  if (!submissionId) {
    submissionId = uuidv4();
  }

  return submissionId;
}

export function resetHvmSubmissionId() {
  submissionId = null;
}

const keepText = text => text;

// `applyVariable` turns null into "null", so only strings go through it.
const toTextReader = applyText => value => typeof value === 'string' && value ? applyText(value) : '';

/**
 * The visible elements of the pages the user actually went through, in the order of the path.
 * Pages of a branch the user went back from stay in the history flagged `isAbandoned` until passed forward again.
 * The current page is not in the history yet.
 */
export function getPathElementIds({ pageHistory = [], currentPageId, currentElementsIds = [] }) {
  const historyElementIds = pageHistory
    .filter(page => page?.id !== currentPageId && !page?.isAbandoned)
    .flatMap(page => (page?.elements ?? []).map(element => element?.id));

  return [...historyElementIds, ...currentElementsIds].filter(Boolean);
}

function getChoiceAnswers(question, applyText) {
  const selectedAnswerIds = question.selectedAnswers ?? [];

  return question.content.answers
    .filter(answer => selectedAnswerIds.includes(answer?.id))
    .map(answer => ({ answerId: answer.id, text: applyText(answer.value) }));
}

function getAmountAnswers(answers, applyText, unit) {
  return answers.map(answer => ({
    answerId: answer?.id,
    text: applyText(answer?.value),
    amount: answer?.amount,
    ...(unit ? { unit } : {}),
  }));
}

function getQuestionTypeAndAnswers(question, applyText) {
  const { content } = question;

  if (Array.isArray(content.answers)) {
    return { type: HvmQuestionType.CHOICE, answers: getChoiceAnswers(question, applyText) };
  }

  if (Array.isArray(content.counterAnswers)) {
    const unit = content.showCurrencySymbol ? content.currencySymbol : undefined;

    return { type: HvmQuestionType.COUNTER, answers: getAmountAnswers(content.counterAnswers, applyText, unit) };
  }

  if (Array.isArray(content.chartAnswers)) {
    return { type: HvmQuestionType.CHART, answers: getAmountAnswers(content.chartAnswers, applyText) };
  }

  return { type: null, answers: [] };
}

/**
 * The answered questions on the user's path as a snapshot: texts are taken as the user saw them, so later edits
 * of the questionnaire do not change a saved result. Answers are read from `allPages`, the history only tells
 * which questions were on the path. Questions without an answer are skipped.
 */
export function getHvmQuestionnaireAnswers({ pagesFlow = {}, pageHistory = [], applyText = keepText }) {
  const readText = toTextReader(applyText);
  const { allPages = [], currentPageId, currentElementsIds } = pagesFlow;
  const questionsById = new Map(
    allPages
      .flatMap(page => page?.elements ?? [])
      .filter(element => element?.templateType === TemplateTypes.QUESTION && element?.content)
      .map(question => [question.id, question])
  );
  const pathQuestionIds = [...new Set(getPathElementIds({ pageHistory, currentPageId, currentElementsIds }))].filter(id => questionsById.has(id));

  return pathQuestionIds
    .map(questionId => {
      const question = questionsById.get(questionId);
      const { type, answers } = getQuestionTypeAndAnswers(question, readText);

      return {
        questionId,
        questionText: readText(question.content.question?.value),
        questionSubline: readText(question.content.questionSubline?.value),
        type,
        multipleChoice: Boolean(question.content.multipleChoice),
        answers,
      };
    })
    .filter(item => item.type && isLength(item.answers))
    .map((item, index) => ({ order: index + 1, ...item }));
}

export function getHvmQuestionnaireResult({ pagesFlow = {}, pageHistory = [], applyText = keepText, sentAt = new Date() }) {
  const { finder } = pagesFlow;

  return {
    submissionId: getHvmSubmissionId(),
    sentAt: sentAt.toISOString(),
    finder: { id: finder?.id, name: finder?.name, url: finder?.url },
    answers: getHvmQuestionnaireAnswers({ pagesFlow, pageHistory, applyText }),
  };
}
